import React from 'react';
import styled from 'styled-components';
import Flyout from '@peloton/flyout';
import { getIsBreakpointLessThan } from '@peloton/responsive';
import { media } from '@peloton/styles';
import { isDefined } from '@peloton/types';
import { track } from '@engage/analytics';
import { BrowseCategorySlug } from '@engage/browse-categories';
import { slate1, white, gray1 } from '@engage/colors';
import { If } from '@engage/conditional-render';
import {
  getAppliedFilterCount,
  clearAll,
  sendAnalyticsBeforeClearAll,
  Source,
  ClosedBySource,
} from '@engage/filters';
import {
  getActiveCategory,
  isLoading,
  getTotalClassesCount,
  requestNewClasses,
  getActiveCategorySlug,
} from '@engage/library';
import { useReduxState, useReduxAction } from '@engage/redux';
import {
  trackViewedClassLanguageModal,
  Source as ClassLanguageSource,
} from '@engage/settings/analytics';
import { spaces } from '@engage/styles';
import { label4Large, capsLabel3Large } from '@engage/typography';
import { Button1Large } from '@members/buttons';
import {
  useClasses,
  Classes,
  ClassesCopy,
  Settings as SettingsKeys,
  useSettings,
} from '@members/copy';
import LoadingSpinner from '@members/loading/LoadingSpinner';
import { Modal } from '@members/modal';
import { modalProps, ClassLanguageModal } from '@members/settings';
import Close from '../icons/Close';
import { ClassLanguageSelect } from './ClassLanguageSelect';
import ClearFilters from './ClearFilters';
import FilterGroups from './FilterGroups';

type Props = {
  toCloseHandler: (closedBySource: ClosedBySource) => () => void;
  open: () => void;
  active: boolean;
};

export const FilterFlyout: React.FC<React.PropsWithChildren<Props>> = ({
  active,
  toCloseHandler: close,
  open,
}) => {
  const appliedFilterCount = useReduxState(getAppliedFilterCount);
  const activeBrowseCategory = useReduxState(getActiveCategory);
  const slug = useReduxState(getActiveCategorySlug);
  const isInitializing = useReduxState(isLoading);
  const totalClasses = useReduxState(getTotalClassesCount);
  const onSendAnalyticsBeforeClearAll = useReduxAction(sendAnalyticsBeforeClearAll);
  const onClearAllFilters = useReduxAction(clearAll);
  const trackClear = React.useCallback(() => {
    onSendAnalyticsBeforeClearAll(Source.FiltersMenu);
    onClearAllFilters(Source.FiltersMenu);
  }, [onSendAnalyticsBeforeClearAll, onClearAllFilters]);
  const onRequestNewClasses = useReduxAction(requestNewClasses);
  const trackAction = useReduxAction(track);
  const [isOpen, setIsOpen] = React.useState(false);
  const openModal = () => {
    trackAction(trackViewedClassLanguageModal(ClassLanguageSource.FiltersMenu));
    setIsOpen(true);
    close(ClosedBySource.LanguageSettings)();
  };
  const closeModal = () => {
    setIsOpen(false);
    setTimeout(() => open(), 400);
  };
  const onSave = () => {
    onRequestNewClasses(slug);
    setIsOpen(false);
    setTimeout(() => open(), 400);
  };
  const isMobile = useReduxState(getIsBreakpointLessThan, 'tablet');
  return (
    <>
      <Flyout
        ariaLabel={useClasses(Classes.Filter)}
        isOpen={active}
        backgroundColor={gray1}
        handleRequestClose={close(ClosedBySource.TapOutside)}
      >
        <FlyoutContent>
          <FlyoutHeader>
            <>
              <CloseButton
                onClick={close(ClosedBySource.Cancel)}
                data-test-id="filterCloseButton"
                aria-label="Close filters"
              >
                <Close />
              </CloseButton>
              <If condition={isDefined(activeBrowseCategory) && isMobile}>
                <BrowseCategoryHeader data-test-id="browseCategoryHeader">
                  {activeBrowseCategory?.name}
                </BrowseCategoryHeader>
              </If>
              <If condition={isMobile && window.location.pathname.includes('all')}>
                <BrowseCategoryHeader data-test-id="browseCategoryHeader">
                  {BrowseCategorySlug.All}
                </BrowseCategoryHeader>
              </If>
            </>
            <ClearFiltersContainer>
              <ClearFilters
                appliedFilterCount={appliedFilterCount}
                onClick={trackClear}
              />
            </ClearFiltersContainer>
          </FlyoutHeader>
          <FilterGroups isFlyoutOpen={active} />
          <ButtonAndLanguageContainer>
            <DoneButton
              appliedFilterCount={appliedFilterCount}
              isInitializing={isInitializing}
              totalClasses={totalClasses}
              close={close(ClosedBySource.ShowClassesButton)}
            />
            <ClassLanguageSelect onOpen={openModal} />
          </ButtonAndLanguageContainer>
        </FlyoutContent>
      </Flyout>
      <Modal
        isOpen={isOpen}
        closeHandler={closeModal}
        contentLabel={useSettings(SettingsKeys.LanguagesModal)}
        {...modalProps}
      >
        <ClassLanguageModal
          closeModal={closeModal}
          onSuccessfulSave={onSave}
          source={ClassLanguageSource.FiltersMenu}
        />
      </Modal>
    </>
  );
};

const StyledButton = styled(Button1Large)`
  height: 44px;
  width: 280px;
  margin-bottom: ${spaces.xxLarge}px;
  ${media.tablet`
    height: 50px;
  `}
`;

type DoneButtonProps = {
  isInitializing: boolean;
  appliedFilterCount: number;
  totalClasses: number;
  close: () => void;
};

const DoneButton: React.FC<React.PropsWithChildren<DoneButtonProps>> = ({
  close,
  appliedFilterCount,
  isInitializing,
  totalClasses,
}) => {
  const disabled = getButtonIsDisabled(isInitializing, totalClasses);
  return (
    <StyledButton data-test-id="selectFiltersButton" onClick={close} disabled={disabled}>
      <ButtonLabel
        isInitializing={isInitializing}
        appliedFilterCount={appliedFilterCount}
        totalClasses={totalClasses}
      />
    </StyledButton>
  );
};

const getStringifiedLabel = (appliedFilterCount: number, totalClasses: number) => {
  if (appliedFilterCount === 0) {
    return <ClassesCopy id={Classes.ShowAllClasses} />;
  }

  if (totalClasses > 1000) {
    const maybeNPlusClasses = (Math.floor(totalClasses / 1000) * 1000).toString();
    return (
      <ClassesCopy
        id={Classes.ShowNPlusClasses}
        values={{
          firstDigit: maybeNPlusClasses.slice(0, maybeNPlusClasses.length - 3),
          lastDigits: maybeNPlusClasses.slice(-3),
        }}
      />
    );
  }

  return <ClassesCopy id={Classes.ShowNClasses} values={{ totalClasses }} />;
};
const ButtonLabel: React.FC<
  React.PropsWithChildren<{
    isInitializing: boolean;
    appliedFilterCount: number;
    totalClasses: number;
  }>
> = ({ isInitializing, appliedFilterCount, totalClasses }) =>
  isInitializing ? (
    <WhiteLoadingSpinner isLoading size={25} />
  ) : (
    <span>{getStringifiedLabel(appliedFilterCount, totalClasses)}</span>
  );
const getButtonIsDisabled = (isInitializing: boolean, totalClasses: number) =>
  !isInitializing && totalClasses === 0;

const FlyoutContent = styled.div`
  display: flex;
  min-height: 100%;
  padding: 0 10px 10px;
  width: 100vw;
  align-items: center;
  flex-direction: column;

  ${media.tablet`
    padding: 0 ${spaces.large}px ${spaces.large}px;
    width: 440px;
  `}
`;

const FlyoutHeader = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: fit-content;
  padding: ${spaces.large}px;
  width: 100vw;
  background-color: ${gray1};
  position: sticky;
  top: 0;
  z-index: 2;

  & > * {
    flex: 1;
  }

  ${media.tablet`
    width: 440px;
  `}
`;

const CloseButton = styled.button`
  ${label4Large};
  color: ${slate1};
  svg {
    height: ${spaces.xLarge}px;
    width: ${spaces.xLarge}px;
  }
`;

const BrowseCategoryHeader = styled.h1`
  ${capsLabel3Large};
  color: ${slate1};
  text-align: center;
`;

const ClearFiltersContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  justify-content: flex-end;
`;

const ButtonAndLanguageContainer = styled.div`
  align-items: center;
  bottom: 0;
  padding-bottom: ${spaces.xxLarge}px;
  display: flex;
  flex-direction: column;
  position: sticky;
  width: 100%;
  background-image: linear-gradient(180deg, rgba(245, 247, 249, 0) 0%, #f5f7f9 35.13%);
`;

const WhiteLoadingSpinner = styled(LoadingSpinner)`
  fill: ${white};
`;
